import { Alert, BrandLogo, Button, Card, Field } from '@catering/ui';
import { Link } from '@tanstack/react-router';
import { type FormEvent, type JSX, useState } from 'react';

import { ApiError } from '../api/client';
import { requestPasswordReset } from './password-reset-api';

const SERVER_ERROR_STATUS = 500;

/**
 * The API answers 202 for any address, registered or not, and this screen
 * keeps that promise: whatever the API says about the address, the person
 * sees the same confirmation. Only server (5xx) and network failures are
 * reported, because they say nothing about any account.
 */
export function ForgotPasswordPage(): JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    const email = readText(new FormData(event.currentTarget), 'email').trim();

    if (email === '') {
      setErrorMessage('Ingresá tu correo electrónico.');

      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await requestPasswordReset(email);
      setIsSent(true);
    } catch (error) {
      // The API answers 202 for any address, so a 5xx or a network failure
      // is a real failure, never a statement about the account.
      if (error instanceof ApiError && error.status < SERVER_ERROR_STATUS) {
        setIsSent(true);
      } else {
        setErrorMessage('No pudimos enviar la solicitud. Intentá de nuevo en unos minutos.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-white to-slate-50 p-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-1">
          <BrandLogo size="lg" />
          <span className="text-sm text-slate-500">Back-office</span>
        </div>

        <Card variant="elevated" className="w-full">
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="flex flex-col gap-5"
            noValidate
          >
            <header>
              <h1 className="text-lg font-semibold text-slate-900">Recuperá tu contraseña</h1>
              <p className="mt-1 text-sm text-slate-500">
                Ingresá tu correo y te enviamos un enlace para elegir una contraseña nueva.
              </p>
            </header>

            {isSent ? (
              <Alert tone="success">
                Si el email está registrado, te enviamos un enlace para restablecer tu contraseña.
                Revisá tu bandeja de entrada.
              </Alert>
            ) : null}

            {errorMessage !== null ? <Alert tone="danger">{errorMessage}</Alert> : null}

            <Field
              label="Correo electrónico"
              name="email"
              type="email"
              autoComplete="username"
              required
              autoFocus
            />

            <Button type="submit" loading={isSubmitting} className="w-full">
              Enviar enlace
            </Button>

            <Link
              to="/login"
              className="text-center text-sm font-medium text-primary-700 underline"
            >
              Volver a iniciar sesión
            </Link>
          </form>
        </Card>
      </div>
    </main>
  );
}

function readText(form: FormData, field: string): string {
  const value = form.get(field);

  return typeof value === 'string' ? value : '';
}
